/**
 * Pure geometry helpers for the 3D scatter plot.
 * Extracted from components/ScatterPlot3D.tsx (2026-04-25) so the math is
 * unit-testable independent of React Three Fiber.
 *
 * The component owns rendering. This file owns:
 *   - mapping ScoredStock → Stock3D (Z-score → world coordinates)
 *   - generating great-circle ring points for the 3σ shell
 *
 * No imports from R3F / three.js — keep this file React-free.
 */

import type { ScoredStock } from './compute'

// 1 Z-score unit = SCALE world units (so 3σ shell renders at radius 6 with SCALE=2)
export const SCALE = 2.6

export interface Stock3D {
  symbol: string
  name: string
  sector: string
  marketCap: number
  quality: number    // raw Z-score (X)
  valuation: number  // raw Z-score (Y)
  technical: number  // momentum composite z-score (Z)
  color: string
  x: number          // quality Z mapped to 3D units
  y: number          // valuation Z mapped to 3D units (negated so expensive=top)
  z: number          // signal Z mapped to 3D units (reduced to 0.75× so Q/V reads clearer)
  dist: number       // distance from origin in Z-score space (pre-clamp)
  radius: number     // sphere render radius (cube-root of market cap)
}

/**
 * Map scored stocks to 3D world positions.
 *
 * 2026-04-24: Z axis uses `momentumScore` (composite of 12-1 price momentum +
 * analyst revisions + earnings surprise), NOT raw RSI. Stocks missing
 * momentumScore render at z=0 rather than being excluded.
 *
 * Anything beyond the 3σ shell is clamped to the surface so the visualization
 * stays bounded.
 */
export function buildStocks3D(
  stocks: ScoredStock[],
  getSectorColor: (sector: string) => string,
): Stock3D[] {
  const valid = stocks.filter(s => s.qualityScore != null && s.valuationScore != null)
  if (valid.length === 0) return []

  return valid.map(s => {
    const tech = s.momentumScore ?? 0
    const q = s.qualityScore!
    const v = s.valuationScore!

    const rawDist = Math.sqrt(q * q + v * v + tech * tech)
    const maxDist = 3
    const clampFactor = rawDist > maxDist ? maxDist / rawDist : 1

    const x = q * clampFactor * SCALE
    const y = -v * clampFactor * SCALE   // negate so expensive=top, cheap=bottom (matches 2D)
    const z = tech * clampFactor * SCALE * 0.75 // reduced so Q/V positioning reads more clearly

    const radius = Math.pow(s.marketCap / 1e9, 1 / 3) * 0.035

    return {
      symbol: s.symbol,
      name: s.name,
      sector: s.sector,
      marketCap: s.marketCap,
      quality: q,
      valuation: v,
      technical: tech,
      color: getSectorColor(s.sector),
      x, y, z, dist: rawDist, radius,
    }
  })
}

/**
 * Generate point list for a great-circle ring on one of the cardinal planes.
 * Used by the 3σ shell wireframe.
 */
export type CircleAxis = 'xy' | 'xz' | 'yz'

export function circlePoints(
  r: number,
  segments: number,
  axis: CircleAxis,
): [number, number, number][] {
  const pts: [number, number, number][] = []
  for (let i = 0; i <= segments; i++) {
    const a = (i / segments) * Math.PI * 2
    const c = Math.cos(a) * r
    const s = Math.sin(a) * r
    if (axis === 'xy') pts.push([c, s, 0])
    else if (axis === 'xz') pts.push([c, 0, s])
    else pts.push([0, c, s])
  }
  return pts
}
